
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Plus, 
  Trash2, 
  Calendar as CalendarIcon, 
  AlertTriangle,
  Clock,
  Check,
  X,
  Edit2
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { ChecklistItem } from '@/hooks/useChecklistItems';
import { ChecklistItemControls } from './ChecklistItemControls';

interface NoteChecklistComponentProps {
  checklistId: string;
  checklistName: string;
  items: ChecklistItem[];
  profiles: any[];
  isNotePrivate: boolean;
  onDelete: () => void;
  onCreateItem: (data: any) => void;
  onUpdateItem: (data: any) => void;
  onDeleteItem: (itemId: string) => void;
  onRenameChecklist: (newName: string) => void;
  progress: number;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
}

export const NoteChecklistComponent = ({
  checklistId,
  checklistName,
  items,
  profiles,
  isNotePrivate,
  onDelete,
  onCreateItem,
  onUpdateItem,
  onDeleteItem,
  onRenameChecklist,
  progress,
  isCreating,
  isUpdating,
  isDeleting
}: NoteChecklistComponentProps) => {
  const [newItemText, setNewItemText] = useState('');
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const [editingName, setEditingName] = useState(checklistName);

  const addItem = () => {
    if (newItemText.trim()) {
      onCreateItem({
        checklist_id: checklistId,
        text: newItemText,
        position: items.length,
      });
      setNewItemText('');
      setIsAddingItem(false);
    }
  };

  const startEditing = (item: ChecklistItem) => {
    setEditingItemId(item.id);
    setEditingText(item.text);
  };

  const saveEdit = () => {
    if (editingItemId && editingText.trim()) {
      onUpdateItem({
        itemId: editingItemId,
        updates: { text: editingText.trim() }
      });
    }
    setEditingItemId(null);
    setEditingText('');
  };

  const saveNameEdit = () => {
    if (editingName.trim() && editingName !== checklistName) {
      onRenameChecklist(editingName.trim());
    }
    setIsEditingName(false);
  };

  const isOverdue = (dueDate?: string) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  const isDueSoon = (dueDate?: string) => {
    if (!dueDate) return false;
    const due = new Date(dueDate);
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return due <= tomorrow && due >= new Date();
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-50 border-red-200';
      case 'medium': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'low': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className="border rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 flex-1">
          {isEditingName ? (
            <div className="flex gap-2 flex-1">
              <Input
                value={editingName}
                onChange={(e) => setEditingName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveNameEdit();
                  if (e.key === 'Escape') {
                    setIsEditingName(false);
                    setEditingName(checklistName);
                  }
                }}
                autoFocus
                className="text-sm font-medium"
              />
              <Button type="button" size="sm" onClick={saveNameEdit}>
                <Check className="h-4 w-4" />
              </Button>
              <Button 
                type="button" 
                variant="ghost" 
                size="sm" 
                onClick={() => {
                  setIsEditingName(false);
                  setEditingName(checklistName);
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <>
              <h5 
                className="font-medium cursor-pointer hover:text-primary"
                onClick={() => setIsEditingName(true)}
              >
                {checklistName}
              </h5>
              <Badge variant="secondary">
                {progress}% ({items.filter(i => i.completed).length}/{items.length})
              </Badge>
              {isNotePrivate && (
                <Badge variant="outline" className="text-xs">
                  Private
                </Badge>
              )}
            </>
          )}
        </div>
        
        {!isEditingName && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onDelete}
            className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        )}
      </div>

      {items.length > 0 && (
        <div className="space-y-3">
          {items.map((item) => (
            <div key={item.id} className="space-y-2">
              <div className="flex items-start gap-2 group">
                <Checkbox
                  checked={item.completed}
                  onCheckedChange={(checked) => 
                    onUpdateItem({
                      itemId: item.id,
                      updates: { completed: !!checked }
                    })
                  }
                  className="mt-1"
                />
                
                <div className="flex-1 space-y-2">
                  {editingItemId === item.id ? (
                    <div className="flex gap-2">
                      <Input
                        value={editingText}
                        onChange={(e) => setEditingText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveEdit();
                          if (e.key === 'Escape') {
                            setEditingItemId(null);
                            setEditingText('');
                          }
                        }}
                        autoFocus
                        className="text-sm"
                      />
                      <Button type="button" size="sm" onClick={saveEdit}>
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => {
                          setEditingItemId(null);
                          setEditingText('');
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <span 
                        className={cn(
                          "text-sm cursor-pointer",
                          item.completed && "line-through text-muted-foreground"
                        )}
                        onClick={() => startEditing(item)}
                      >
                        {item.text}
                      </span>
                      
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => startEditing(item)}
                          className="h-6 w-6 p-0"
                        >
                          <Edit2 className="h-3 w-3" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => onDeleteItem(item.id)}
                          className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Item metadata */}
                  <div className="flex items-center gap-2 text-xs flex-wrap">
                    {/* Priority */}
                    <Badge 
                      variant="outline" 
                      className={cn("text-xs", getPriorityColor(item.priority))}
                    >
                      {item.priority}
                    </Badge>

                    {/* Due date with visual indicators */}
                    {item.due_date && (
                      <div className={cn(
                        "flex items-center gap-1 px-2 py-1 rounded",
                        isOverdue(item.due_date) && !item.completed && "bg-red-50 text-red-700",
                        isDueSoon(item.due_date) && !item.completed && "bg-yellow-50 text-yellow-700"
                      )}>
                        {isOverdue(item.due_date) && !item.completed && (
                          <AlertTriangle className="h-3 w-3" />
                        )}
                        {isDueSoon(item.due_date) && !item.completed && (
                          <Clock className="h-3 w-3" />
                        )}
                        <CalendarIcon className="h-3 w-3" />
                        <span>{format(new Date(item.due_date), 'MMM d')}</span>
                      </div>
                    )}
                  </div>

                  {/* Assignment and deadline controls */}
                  <ChecklistItemControls
                    item={item}
                    profiles={profiles}
                    onUpdate={(updates) => onUpdateItem({ itemId: item.id, updates })}
                    isNotePrivate={isNotePrivate}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {isAddingItem ? (
        <div className="flex gap-2">
          <Input
            placeholder="Add item..."
            value={newItemText}
            onChange={(e) => setNewItemText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') addItem();
              if (e.key === 'Escape') {
                setIsAddingItem(false);
                setNewItemText('');
              }
            }}
            autoFocus
            className="text-sm"
          />
          <Button type="button" size="sm" onClick={addItem} disabled={isCreating}>
            <Check className="h-4 w-4" />
          </Button>
          <Button 
            type="button" 
            variant="ghost" 
            size="sm" 
            onClick={() => {
              setIsAddingItem(false);
              setNewItemText('');
            }}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setIsAddingItem(true)}
          className="w-full justify-start text-muted-foreground"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add item
        </Button>
      )}
    </div>
  );
};
